import { getThinClientUrl } from "../../utils/network/BaseURLUtils";
import { Logger } from "@amzn/dolphin-web-framework";
import { chainWalk } from "../../utils/utils";
import { v4 as uuidv4 } from "uuid";
import AppDataStore from "../app-data";

// WebSocket reconnection interval (in milliseconds)
const RECONNECT_INTERVAL = 1000;
// Constant for acknowledging received messages
const ACK = "Ack";

/**
 * Manages WebSocket communication with Thin Client for automation systems (e.g., SBL)
 * Encapsulates connection lifecycle, message handling, and acknowledgment logic.
 */
class ThinClientCommunicator {
    constructor() {
        this.webSocketConnection = null;
        this.connectionPromise = null;
        this.promiseHandlers = {};
    }

    /**
     * Initializes the WebSocket connection if not already active.
     * Handles all lifecycle events: open, error, message, close.
     */
    init() {
        if (this.webSocketConnection) return;

        let wsUrl;
        try {
            wsUrl = getThinClientUrl();
            this.webSocketConnection = new WebSocket(wsUrl);
        } catch (err) {
            Logger.log.error('[IoT-Automation] Invalid WebSocket URL:', err);
            if (this.promiseHandlers.reject) this.promiseHandlers.reject(err);
            return;
        }

        const startTime = Date.now();

        // When connection is successfully opened
        this.webSocketConnection.onopen = () => {
            Logger.log.info('[IoT-Automation] Connected! Latency(ms): ' + (Date.now() - startTime));
            AppDataStore.setIsAutomationTCAvailable(true);
            if (this.promiseHandlers.resolve) this.promiseHandlers.resolve();
        };

        // When connection encounters an error
        this.webSocketConnection.onerror = (error) => {
            Logger.log.error('[IoT-Automation] WebSocket error:', error);
            if (this.promiseHandlers.reject) this.promiseHandlers.reject(error);
            this.webSocketConnection = null;
        };

        // When a message is received from Thin Client
        this.webSocketConnection.onmessage = (event) => {
            try {
                const eventData = JSON.parse(event.data);
                Logger.log.info('[IoT-Automation] Message from thin client: ' + event.data);
                this.sendAcknowledgement(eventData); // Send ACK for every received event
            } catch (e) {
                Logger.log.error('[IoT-Automation] Error processing message:', e);
            }
        };

        // When the connection is closed (unexpected or manual)
        this.webSocketConnection.onclose = (event) => {
            Logger.log.warn(`[IoT-Automation] Connection closed${event.code !== 1000 ? ` with code ${event.code}` : ''}`);
            this.webSocketConnection = null;

            if (AppDataStore.isAutomationTCAvailable()) {
                setTimeout(() => this.init(), RECONNECT_INTERVAL);
            }
        };
    }

    /**
     * Sends an ACK back to the Thin Client to confirm receipt of a message.
     * @param {Object} eventData - The message/event received from Thin Client
     */
    sendAcknowledgement(eventData) {
        const ack = {
            eventName: eventData.type,
            processPath: ACK,
            payload: {
                requestId: chainWalk(() => eventData.requestId, uuidv4()),
                thinClientMessageSendTimestamp: chainWalk(() => eventData.thinClientMessageSendTimestamp, null),
                dolphinMessageSendTimestamp: Date.now()
            }
        };
        this.sendMessage(ack);
    }

    /**
     * Connects the WebSocket and returns a promise.
     */
    connectWebSocket() {
        this.connectionPromise = new Promise((resolve, reject) => {
            this.promiseHandlers = { resolve, reject };
        });
        this.init();
        return this.connectionPromise;
    }

    /**
     * Sends a message to the Thin Client.
     * @param {Object} message - Message object
     * @returns {boolean} - true if sent, false otherwise
     */
    sendMessage(message) {
        if (this.webSocketConnection?.readyState === WebSocket.OPEN) {
            this.webSocketConnection.send(JSON.stringify(message));
            return true;
        } else {
            Logger.log.error('[IoT-Automation] Cannot send message: connection not open');
            return false;
        }
    }

    /**
     * Closes the WebSocket connection.
     */
    closeWebSocketConnection() {
        if (this.webSocketConnection?.readyState === WebSocket.OPEN) {
            this.webSocketConnection.close();
        }
    }

    /**
     * Returns the current WebSocket instance.
     */
    getWebSocket() {
        return this.webSocketConnection;
    }
}

export default new ThinClientCommunicator();
